import React, { useEffect, useState, useMemo } from "react";
import { KeyboardAvoidingView, Linking, Platform, View, useWindowDimensions, ScrollView, BackHandler, Image, Modal } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Popover from "react-native-popover-view/dist/Popover";
import { printFile } from '../../platform/printer/printer';
import Svg, { Rect, G } from 'react-native-svg';
import { generateBarcodeDetails } from "../../utils/orderReceiptGenerator";

import { config } from "../../shared/environment";
//common components
import CommonHeader from "../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
//service
import CommonService from "../../shared/services/commonService";
//module
import { useAppNavigation, useAppRoute } from "../../types/@react-navigation/navigation";
//redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { pageDirty } from "../../redux/slices/commonSlice";
import { getOneCustomerDetails, printMembershipCartApi } from "../../redux/slices/customerSlice";
//shared components
import { ZenBadge, ZenButton, ZenDivider, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast } from "zen-core-elements";
import { CustomModal } from "../../shared/commonComponents/customModal";


const PrintMembershipCard = () => {
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    //variable to store windowsDimentions
    const { width } = useWindowDimensions()
    //variable to store useNavigation values 
    const navigation = useAppNavigation()
    //variable to store useRoute values 
    const { params } = useAppRoute("PrintMembershipCard")
    //variable to store redux dispatch 
    const dispatch = useAppDispatch()
    //variable to handle preview state
    const [preview, setPreview] = useState(false)
    //variable to store input container width
    const [inputWidth, setInputWidth] = useState<"100%" | "50%">("100%")
    //variable to handle loader
    const [isLoader, setIsLoader] = useState(true)
    //variable to handle leave popover
    const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
    //variable to store terms and condition value
    let termsAndCondition = ["This is a membership card, not a credit card.", "This card is only valid in india,", "This card is the property of ZenBasket Pvt Ltd.", "If found please return to ZenBasket Pvt Ltd,Vinayagar Street, NGO A Colony, Tirunelveli - 627007", "Please report the loss of this card immediately to the store"]
    //Variable to store Contact formik initial Values
    const [formikInitialValues, setFormikInitialValues] = useState({ firstName: "", lastName: "", customerCode: "", email: "", phoneNumber: "", balance: "", earned: "", spend: "" })
    //variable to handle transparent loader
    const [transparentLoader, setTransparentLoader] = useState(false)
    const [svg, setSvg] = useState('');

    const barcodeDetails = useMemo(() => {
        return generateBarcodeDetails(formik?.values?.customerCode || "Not-Found", 40, 1.4, "CODE128");
    }, [formik?.values?.customerCode]);

    useEffect(() => {
        getOneCustomerDetailsApi()
    }, [params?.customerId])
    //useEffect triggered  when dimention(width) change 
    useEffect(() => {
        if (width > 600) {
            setInputWidth("50%")
        } else {
            setInputWidth("100%")
        }
    }, [width])

    //Method for get one customer details
    const getOneCustomerDetailsApi = async () => {
        await dispatch(getOneCustomerDetails({ storeId: config?.storeId, customerId: params?.customerId })).unwrap().then((response) => {
            if (response && response?.customerDetails) {
                let customerDetails = response?.customerDetails
                setFormikInitialValues({ firstName: customerDetails?.user?.firstName, lastName: customerDetails?.user?.lastName, customerCode: customerDetails?.customerCode ? customerDetails?.customerCode : "", email: customerDetails?.user?.email, phoneNumber: customerDetails?.user?.phone, balance: customerDetails?.balancePoints ? customerDetails?.balancePoints : "", earned: "", spend: "" })
                setIsLoader(false)
            }
        }).catch((error: any) => {
            console?.log("Get One Customer Details", error)
        })
    }
    //Method for formik validation
    const yupValidation = Yup.object().shape({
        firstName: Yup.string().required('First name is required')
            .max(30, "Maxmimum length of first name exceed")
            .matches(/^[a-zA-Z ]+$/, 'First Name accepts characters only'),
        lastName: Yup.string()
            .max(30, "Maxmimum length of last name exceed")
            .matches(/^[a-zA-Z ]+$/, 'Last Name accepts characters only'),
        customerCode: Yup.string().required("Customer code required")
    })

    //Formik
    const formik = useFormik({
        initialValues: formikInitialValues,
        validationSchema: yupValidation,
        enableReinitialize: true,
        onSubmit: () => {
            console.log("form submit")
        }
    })
    // useEffect to open popover on backpress
    useEffect(() => {
        BackHandler.addEventListener('hardwareBackPress', function () {
            if (formik?.dirty) {
                setLeavePopoverVisible(true)
            }
            else {
                navigation.goBack()
            }
            return true
        })
        if (formik?.dirty) {
            dispatch(pageDirty(true))
        } else {
            dispatch(pageDirty(false))
        }
    }, [formik?.dirty])

    //Membership card design component
    const PreviewComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }: { theme: Theme }) => (
                    <View style={[theme?.printMembershipCardStyle?.cardOuterContainer, { flexDirection: width > 600 ? "row" : "column", }]}>
                        <View style={[theme?.printMembershipCardStyle?.cardInnerContainer, { marginHorizontal: width > 600 ? 0 : 15, height: 220, width: width > 600 ? "49%" : undefined }]}>
                            <View style={[theme?.printMembershipCardStyle?.frontViewTextstyle, { padding: width > 600 ? 15 : 12 }]}>
                                <ZenText content={"MemberShip/Loyalty card"} size={theme?.mediumFontSize} color={theme?.light} bold />
                            </View>
                            <View style={theme?.printMembershipCardStyle?.frontViewSecondConatiner}>
                                <View style={theme?.printMembershipCardStyle?.previewContainer}>
                                    <View style={{ width: "30%", alignItems: "center" }}>
                                        <Image source={{ uri: "https://bloggallery.s3.us-west-2.amazonaws.com/Mobile/306669425_522650896529494_3025486609798314771_n-removebg-preview_1708063112621.png" }} resizeMode="cover" style={{ width: 120, height: 70, }} />
                                    </View>
                                    <View style={{ width: "70%", marginHorizontal: 15 }}>
                                        <ZenText content={"Name"} color={theme?.labelColor} style={{ paddingTop: 5 }} />
                                        <ZenText content={formik?.values?.firstName + " " + formik?.values?.lastName} bold color={theme?.dark} style={{ paddingTop: 5 }} />
                                    </View>
                                </View>
                                <ZenDivider style={{ backgroundColor: theme?.lightGrey, height: 0.4, marginVertical: 5 }} />
                                <View style={[theme?.printMembershipCardStyle?.previewContainer, { marginTop: 10 }]}>
                                    <View style={[theme?.printMembershipCardStyle?.customerCodeView, { columnGap: 5 }]}>
                                        <ZenText content={"Code:"} color={theme?.labelColor} />
                                        <ZenBadge
                                            badgeName={formik?.values?.customerCode}
                                            badgeColor={theme?.badgeColor}
                                            badgeTextColor={theme.light}
                                            badgeTextSize={"small"}
                                            badgeTextStyle={{ fontFamily: theme.bold }}
                                            badgeContainerStyle={{ alignSelf: "center" }}
                                        />
                                    </View>
                                    <View style={{ width: 140, height: 40, alignItems: 'center', justifyContent: 'center' }}>
                                        {barcodeDetails && barcodeDetails.bars.length > 0 ? (
                                            <Svg
                                                width={140}
                                                height={40}
                                                viewBox={`0 0 ${barcodeDetails.totalWidth} ${barcodeDetails.height}`}
                                            >
                                                <Rect width="100%" height="100%" fill="#ffffff" />
                                                <G fill="#000000">
                                                    {barcodeDetails.bars.map((bar, index) => (
                                                        <Rect
                                                            key={index}
                                                            x={bar.x}
                                                            y={0}
                                                            width={bar.width}
                                                            height={barcodeDetails.height}
                                                        />
                                                    ))}
                                                </G>
                                            </Svg>
                                        ) : null}
                                    </View>
                                </View>
                            </View>
                        </View>
                        <View style={[theme?.printMembershipCardStyle?.cardInnerContainer, { marginHorizontal: width > 600 ? 0 : 15, height: 220, width: width > 600 ? "49%" : undefined }]}>
                            <View style={{ padding: 10 }}>
                                <ZenText content={"Terms and condition"} color={theme?.dark} size={theme?.smallHeaderFontSize} />
                                {termsAndCondition?.map((item, index) => (
                                    <View key={index} style={theme?.printMembershipCardStyle?.termsAndConditionTextStyle}>
                                        <ZenIcon name="dot-single" type="entypo" size={16} color={theme?.grey} />
                                        <ZenText content={item} color={theme?.dark} size={theme?.badgeFontSize} />
                                    </View>
                                ))}
                                <ZenDivider style={{ backgroundColor: theme?.lightGrey, height: 0.4, marginBottom: 5 }} />
                                <View style={{ paddingTop: 5 }}>
                                    <ZenText content="For detailed terms and conditions" color={theme?.dark} size={theme?.badgeFontSize} />
                                    <View style={theme?.printMembershipCardStyle?.customerCodeView}>
                                        <ZenText content="Please visit " color={theme?.dark} size={theme?.badgeFontSize} />
                                        <ZenText content="www.getzenbasket.com" color={theme?.dark} style={{ textDecorationLine: "underline" }} size={theme?.badgeFontSize} bold onPress={() => Linking.openURL("https://getzenbasket.com/")} />
                                    </View>
                                </View>
                                <ZenDivider style={{ backgroundColor: theme?.lightGrey, height: 0.4, marginVertical: 5 }} />
                                <View>
                                    <View style={theme?.printMembershipCardStyle?.customerCodeView}>
                                        <ZenText content="Email" color={theme?.dark} style={{ paddingRight: 5 }} size={theme?.smallHeaderFontSize} />
                                        <ZenText content="contact@getzenbasket.com" color={theme?.dark} size={theme?.smallHeaderFontSize} bold onPress={() => Linking.openURL("https://getzenbasket.com/")} />
                                    </View>
                                    <View style={theme?.printMembershipCardStyle?.customerCodeView}>
                                        <ZenText content="Call us" color={theme?.dark} style={{ paddingRight: 5 }} size={theme?.smallHeaderFontSize} />
                                        <ZenText content="+91 7894563210" color={theme?.dark} size={theme?.smallHeaderFontSize} bold onPress={() => Linking.openURL("https://getzenbasket.com/")} />
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                )
                }
            </ThemeConsumer >
        )
    }

    //method for popover confirmation 
    const confirmation = (item: { id: number }) => {
        setLeavePopoverVisible(false)
        if (item?.id == 2) {
            navigation?.goBack()
            dispatch(pageDirty(false))
        }
    }
    const handlePreview = () => {
        if (formik?.isValid && formik?.values?.customerCode != "")
            setPreview(!preview)
        else {
            formik?.handleSubmit()
            ZenToast({ type: "error", message: "Please fill out the mandatory fields" })
        }
    }
    const handleViewChanges = () => {
        if (formik?.isValid)
            setPreview(!preview)
        else {
            formik?.handleSubmit()
            ZenToast({ type: "error", message: "Please fill out the mandatory fields" })
        }
    }
    const printMembershipCard = async () => {
        setTransparentLoader(true)
        await dispatch(printMembershipCartApi({
            storeId: config.storeId,
            customerId: params?.customerId,
            data: {
                firstName: formik?.values?.firstName,
                lastName: formik?.values?.lastName
            }
        })).unwrap().then(async (response) => {
            if (response?.success) {
                await printFile(response?.invoice, { isLandscape: false })
            } else {
                ZenToast({ type: "error", message: "Failed to create membership card. Please contact the admin for assistance" })
            }
        }).catch(() => {
            ZenToast({ type: "error", message: "Something went wrong, Please try again later!!" })
        }).finally(() => {
            setTransparentLoader(false)
        })
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={theme?.addEditCustomerStyle?.outerContainer}>
                    <CommonHeader headerName="MemberShip/Loyalty card" backIcon backIconOnpress={() => { formik?.dirty ? setLeavePopoverVisible(true) : navigation?.goBack() }} rightComponent={(isLoader || width < 600) ? undefined :
                        <ZenButton
                            title="Print"
                            onPress={() => {
                                printMembershipCard()
                            }}
                            disabled={!formik?.isValid}
                            titleColor={theme?.light}
                            buttonStyle={theme?.printMembershipCardStyle?.printButtonStyle}
                        />} />
                    {isLoader ?
                        <ZenLoader viewStyle={{ flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: theme?.lightBackgroundColor }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                        : null}
                    {isLoader ? null : width > 600 ? null : <View style={[theme?.printMembershipCardStyle?.buttonsOuterContainer, { marginHorizontal: width > 600 ? 15 : 10, columnGap: 10 }]}>
                        <ZenButton
                            title={!preview ? "Preview" : "Back"}
                            onPress={handlePreview}
                            titleColor={theme?.primary}
                            type="outline"
                            outlineColor={theme?.primary}
                            buttonStyle={{ borderRadius: 10, backgroundColor: theme?.light }}
                            containerStyle={{ borderRadius: 10 }}
                        />
                        <ZenButton
                            title="Print"
                            onPress={() => { console.log("select print") }}
                            titleColor={theme?.light}
                            buttonStyle={theme?.printMembershipCardStyle?.printButtonStyle}
                        />
                    </View>}
                    {isLoader ? null : (!preview || width > 600) ? <ScrollView>
                        <View style={[theme?.printMembershipCardStyle?.outerContainer, { marginHorizontal: width > 600 ? 15 : 10, marginTop: width > 600 ? 15 : 10 }]}>
                            <View style={{ flexDirection: width > 600 ? "column" : "row", justifyContent: "space-around" }}>
                                <View style={[theme?.printMembershipCardStyle?.firstContainer, width > 600 && { flexDirection: "row", flexWrap: "wrap" }]}>
                                    <View style={{ width: inputWidth }}>
                                        <ZenInput
                                            value={formik?.values?.firstName}
                                            onChangeText={(text) => formik?.setFieldValue("firstName", text)}
                                            label={"First Name*"}
                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                            onBlur={() => { formik?.setFieldTouched("firstName", true) }}
                                            onEndEditing={() => { formik.setFieldValue("firstName", formik?.values?.firstName?.trim()) }}
                                            errorMessage={formik?.errors?.firstName && !formik?.isValid && formik?.touched?.firstName ? formik?.errors?.firstName : ''}
                                            errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: formik?.errors?.firstName && !formik?.isValid && formik?.touched?.firstName ? 10 : 0 }}
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 15, }}
                                            inputContainerStyle={{ paddingHorizontal: 8 }}
                                        />
                                    </View>
                                    <View style={{ width: inputWidth }}>
                                        <ZenInput
                                            value={formik?.values?.lastName}
                                            onChangeText={(text) => formik?.setFieldValue("lastName", text)}
                                            label={"Last Name"}
                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                            onBlur={() => { formik?.setFieldTouched("lastName", true) }}
                                            onEndEditing={() => { formik.setFieldValue("lastName", formik?.values?.lastName?.trim()) }}
                                            errorMessage={formik?.errors?.lastName && !formik?.isValid && formik?.touched?.lastName ? formik?.errors?.lastName : ''}
                                            errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: formik?.errors?.lastName && !formik?.isValid && formik?.touched?.lastName ? 10 : 0 }}
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 15, }}
                                            inputContainerStyle={{ paddingHorizontal: 8 }}
                                        />
                                    </View>
                                    <View style={{ width: inputWidth }}>
                                        <ZenInput
                                            value={formik?.values?.customerCode}
                                            onChangeText={(text) => { console.log(text) }}
                                            label={"Customer Code*"}
                                            editable={false}
                                            disabled={true}
                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                            errorMessage={formik?.errors?.customerCode && !formik?.isValid ? formik?.errors?.customerCode : ''}
                                            errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: formik?.errors?.customerCode && !formik?.isValid ? 10 : 0 }}
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 15, }}
                                            inputContainerStyle={{ paddingHorizontal: 8 }}
                                        />
                                    </View>
                                    <View style={{ width: inputWidth }}>
                                        <ZenInput
                                            value={formik?.values?.email}
                                            onChangeText={(text) => { console.group(text) }}
                                            label={"Email"}
                                            editable={false}
                                            disabled={true}
                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 15, }}
                                            inputContainerStyle={{ paddingHorizontal: 8 }}
                                        />
                                    </View>
                                    <View style={{ width: inputWidth }}>
                                        <ZenInput
                                            value={formik?.values?.phoneNumber}
                                            onChangeText={(text) => { console.log(text) }}
                                            label={"Phone Number"}
                                            editable={false}
                                            disabled={true}
                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                            type="outlined"
                                            containerStyle={{ paddingHorizontal: 15, }}
                                            inputContainerStyle={{ paddingHorizontal: 8 }}
                                        />
                                    </View>
                                    <View style={{ marginHorizontal: 15, width: width > 600 ? "45%" : undefined }}>
                                        <ZenText content="Loyalty Details" style={theme?.addEditCustomerStyle?.labelStyle} />
                                        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: 5 }}>
                                            <View style={{ rowGap: 5 }}>
                                                <ZenText content="Balance amount" style={{ color: theme?.lightDark }} size={theme.smallFontSize} />
                                                <ZenBadge
                                                    badgeName={CommonService?.currencyStandard(currencySymbol, formik?.values?.balance ? formik?.values?.balance : "0")}
                                                    badgeColor={theme?.customerStatus?.typeWholesalecolor}
                                                    badgeTextColor={theme.light}
                                                    badgeTextSize={"small"}
                                                    badgeTextStyle={{ fontFamily: theme.bold }}
                                                    badgeContainerStyle={{ alignSelf: "center" }}
                                                />
                                            </View>
                                            <View style={{ rowGap: 5 }}>
                                                <ZenText content="Total earned" style={{ color: theme?.lightDark }} size={theme.smallFontSize} />
                                                <ZenBadge
                                                    badgeName={CommonService?.currencyStandard(currencySymbol, formik?.values?.earned ? formik?.values?.earned : "0")}
                                                    badgeColor={theme?.badgeColor}
                                                    badgeTextColor={theme.light}
                                                    badgeTextSize={"small"}
                                                    badgeTextStyle={{ fontFamily: theme.bold }}
                                                    badgeContainerStyle={{ alignSelf: "center" }}
                                                />
                                            </View>
                                            <View style={{ rowGap: 5 }}>
                                                <ZenText content="Total spend" style={{ color: theme?.lightDark }} size={theme.smallFontSize} />
                                                <ZenBadge
                                                    badgeName={CommonService?.currencyStandard(currencySymbol, formik?.values?.spend ? formik?.values?.spend : "0")}
                                                    badgeColor={theme?.customerStatus?.declinedStatus}
                                                    badgeTextColor={theme.light}
                                                    badgeTextSize={"small"}
                                                    badgeTextStyle={{ fontFamily: theme.bold }}
                                                    badgeContainerStyle={{ alignSelf: "center" }}
                                                />
                                            </View>
                                        </View>
                                    </View>
                                </View>
                                {width > 600 ? <View style={theme?.printMembershipCardStyle?.secondContainer}>
                                    <View style={theme?.printMembershipCardStyle?.previewContainer}>
                                        <ZenText content={"Preview"} color={theme?.dark} style={{ marginVertical: 10 }} bold size={theme?.headerFontSize} />
                                        <ZenButton
                                            title={"View changes"}
                                            onPress={handleViewChanges}
                                            titleColor={theme?.primary}
                                            type="outline"
                                            outlineColor={theme?.primary}
                                            buttonStyle={{ borderRadius: 10, backgroundColor: theme?.light }}
                                            containerStyle={{ borderRadius: 10 }}
                                        />
                                    </View>
                                    <PreviewComponent />
                                </View> : null}
                            </View>
                        </View>
                    </ScrollView> :
                        <PreviewComponent />}
                    {/* For leave confirmation */}
                    <Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
                        <CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
                    </Popover>
                    <CustomModal visible={transparentLoader} transparent>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                    </CustomModal>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )
}


export default PrintMembershipCard